/**
 * Agent installs in the console (R-ON-9, ADR 0099): state.ext['agent-install'] from the hub, kept
 * live by SSE 'ext', and Install. In mock mode the demo machines report what they can install and
 * an Install runs through its phases in memory.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AGENT_INSTALL_EXT, AGENT_INSTALL_PATH, AGENT_INSTALL_SETTINGS_DEFAULT } from '@orgus/protocol';
import type { AgentInstallHubState, AgentInstallPhase, AgentInstallRecord, InstallableAgentKind, Machine } from '@orgus/protocol';
import { apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const now = () => useOrgus.getState().ext[AGENT_INSTALL_EXT] as AgentInstallHubState | undefined;
const put = (s: AgentInstallHubState) => useOrgus.getState().applyExt({ ext: AGENT_INSTALL_EXT, type: 'state', data: s });

/** A machine that joins the demo (Add machine): Claude Code there, Codex to install, Gemini CLI needs Node.js. */
function joinedStatus(at: number): AgentInstallHubState['status'][string] {
  return {
    checkedAt: at,
    kinds: [
      { kind: 'claude-code', installed: true, version: '2.1.4', installable: false },
      { kind: 'codex', installed: false, version: null, installable: true },
      { kind: 'gemini-cli', installed: false, version: null, installable: false, blocked: 'needs-node' },
    ],
  };
}

/**
 * The demo: studio has all three agents; mbp-16 has Claude Code and Codex, and Gemini CLI can be
 * installed there; build-01 is offline and says nothing; a machine that joins through Add machine
 * can have Codex installed and needs Node.js for Gemini CLI.
 */
export function mockAgentInstallState(machines: Pick<Machine, 'id' | 'name' | 'online'>[], at: number): AgentInstallHubState {
  const status: AgentInstallHubState['status'] = {};
  for (const m of machines) {
    if (m.name === 'studio') {
      status[m.id] = {
        checkedAt: at,
        kinds: [
          { kind: 'claude-code', installed: true, version: '2.1.3', installable: false },
          { kind: 'codex', installed: true, version: '0.46.0', installable: false },
          { kind: 'gemini-cli', installed: true, version: '0.58.0', installable: false },
        ],
      };
    } else if (m.name === 'mbp-16') {
      status[m.id] = {
        checkedAt: at,
        kinds: [
          { kind: 'claude-code', installed: true, version: '2.1.3', installable: false },
          { kind: 'codex', installed: true, version: '0.46.0', installable: false },
          { kind: 'gemini-cli', installed: false, version: null, installable: true },
        ],
      };
    } else if (m.online && m.name !== 'build-01') {
      status[m.id] = joinedStatus(at);
    }
  }
  return { installs: [], status, settings: { ...AGENT_INSTALL_SETTINGS_DEFAULT } };
}

/** A machine joined the demo after it was seeded: it reports what it can install. */
export function mockAgentInstallJoined(machineId: string, at: number): void {
  const cur = now();
  if (cur) put({ ...cur, status: { ...cur.status, [machineId]: joinedStatus(at) } });
}

/** In mock mode, seed the demo once the demo state is ready. */
function useMockSeed(): void {
  const { ready, mock, missing } = useOrgus(useShallow((s) => ({ ready: s.phase === 'ready', mock: s.mode === 'mock', missing: !s.ext[AGENT_INSTALL_EXT] })));
  useEffect(() => {
    if (!mock || !ready || !missing) return;
    put(mockAgentInstallState(Object.values(useOrgus.getState().machines), Date.now()));
  }, [mock, ready, missing]);
}

/** The module's state, or null on a hub without it (an agent that cannot install shows no Install). */
export function useAgentInstall(): AgentInstallHubState | null {
  useMockSeed();
  return useOrgus((s) => (s.ext[AGENT_INSTALL_EXT] as AgentInstallHubState | undefined) ?? null);
}

/** In mock mode an install walks through its phases, then the agent counts as installed. */
function mockInstall(machineId: string, kind: InstallableAgentKind): AgentInstallRecord {
  const at = Date.now();
  const rec: AgentInstallRecord = { id: `inst_mock_${at}`, machineId, kind, state: 'installing', phase: 'checking', requestedBy: 'owner', auto: false, requestedAt: at, finishedAt: null, version: null };
  const update = (patch: Partial<AgentInstallRecord>, installed = false) => {
    const cur = now();
    if (!cur) return;
    const installs = cur.installs.map((r) => (r.id === rec.id ? { ...r, ...patch } : r));
    const st = cur.status[machineId];
    const status = installed && st ? { ...cur.status, [machineId]: { ...st, kinds: st.kinds.map((k) => (k.kind === kind ? { ...k, installed: true, installable: false, version: patch.version ?? null } : k)) } } : cur.status;
    put({ ...cur, installs, status });
  };
  const total = 48_000_000;
  const steps: [number, Partial<AgentInstallRecord>][] = [
    [600, { phase: 'downloading' as AgentInstallPhase, bytes: 0, total }],
    [1400, { phase: 'downloading', bytes: total * 0.4, total }],
    [2200, { phase: 'downloading', bytes: total * 0.85, total }],
    [2800, { phase: 'verifying', bytes: undefined, total: undefined }],
    [3400, { phase: 'installing' }],
    [4200, { phase: 'checking-version' }],
  ];
  for (const [ms, patch] of steps) setTimeout(() => update(patch), ms);
  setTimeout(() => update({ state: 'installed', phase: null, finishedAt: Date.now(), version: '0.65.0' }, true), 4800);
  const cur = now();
  if (cur) put({ ...cur, installs: [rec, ...cur.installs] });
  return rec;
}

/** Ask the hub to install `kind` on the machine. Errors carry what failed, why and what to do next. */
export async function startInstall(machineId: string, kind: InstallableAgentKind): Promise<AgentInstallRecord> {
  if (isMock()) return mockInstall(machineId, kind);
  const r = await apiPost<{ install: AgentInstallRecord }>(AGENT_INSTALL_PATH, { machineId, kind });
  return r.install;
}
